import React, { useContext, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Tooltip } from "@mui/material";
import { ConfirmationDialog } from "../../../common/ConfirmationDialog";
import {
  ApiErrorHandler,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import {
  IBroadcastChats,
  IBroadcastInfo,
  IGroupInfo,
  INonPatientChats,
  IPatientChats,
  IUserInfo,
} from "@home-health-notify/domain";
import { useSnackbar } from "notistack";
import { RemoveMemberButton } from "./Members.style";

type RemoveMemberProps = {
  userInfo: IUserInfo;
  groupInfo: IGroupInfo | IBroadcastInfo;
};

export const RemoveMember = ({ userInfo, groupInfo }: RemoveMemberProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const tNsDrawer = (key: string) => t(key, { ns: "drawerHeader" });

  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);

  const openConfirmationDialog = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsConfirmationDialogOpen(true);
  };

  const onCloseConfirmationDialog = () => {
    setIsConfirmationDialogOpen(false);
  };

  const onRemoveMember = async () => {
    try {
      const successMessage = `${userInfo.name} ${tNsDrawer("removeMemberSuccessMessage")}`;
      const chatManager =
        selectedOrgUser?.chatManager.getChatManagerForGroupType(
          groupInfo.groupType,
        ) as IPatientChats | INonPatientChats | IBroadcastChats;

      await chatManager.removeMember({
        id: groupInfo.id,
        members: [userInfo.id],
      });

      enqueueSnackbar(successMessage, {
        autoHideDuration: 3000,
      });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    }
  };

  return (
    <Box style={{ marginLeft: "auto" }}>
      <Tooltip title={tNs("removeMember")} arrow>
        <RemoveMemberButton
          onClick={openConfirmationDialog}
          data-testid="remove-member-button"
        >
          <DeleteIcon />
        </RemoveMemberButton>
      </Tooltip>
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={onCloseConfirmationDialog}
        confirmationTitle={tNs("removeMemberTitle")}
        confirmationMessage={tNs("confirmRemoveMember")}
        confirmationButtonText={tNs("remove")}
        onConfirmationAction={onRemoveMember}
      />
    </Box>
  );
};
